import { getApiBaseUrl } from '../lib/api';
import React, { useState } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Calculator, TrendingDown, CheckCircle, ArrowRight, PoundSterling } from 'lucide-react';

const API_URL = getApiBaseUrl();

export default function SavingsCalculator() {
  const [inputs, setInputs] = useState({
    monthly_rent: 1200,
    num_properties: 1,
    current_management_fee_percent: 10,
    has_compliance_issues: false,
    num_repairs_per_year: 4,
    average_repair_cost: 200,
    current_void_days_per_year: 30,
    uses_traditional_inspection: true
  });
  
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);

  const calculate = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/calculator/savings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(inputs)
      });
      
      if (response.ok) {
        const data = await response.json();
        setResults(data);
      }
    } catch (error) {
      console.error('Calculation failed:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  return (
    <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-2xl p-6 md:p-8">
      <div className="flex items-center gap-3 mb-6">
        <div className="p-3 bg-emerald-600 rounded-xl">
          <Calculator className="w-6 h-6 text-white" />
        </div>
        <div>
          <h2 className="text-xl md:text-2xl font-bold text-gray-900">
            Savings Calculator
          </h2>
          <p className="text-gray-600 text-sm">See how much you could save</p>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {/* Input Section */}
        <Card className="p-5">
          <h3 className="font-semibold mb-4">Your Property Details</h3>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Monthly Rent (per property)
              </label>
              <div className="relative">
                <PoundSterling className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="number"
                  value={inputs.monthly_rent}
                  onChange={(e) => setInputs({...inputs, monthly_rent: parseFloat(e.target.value) || 0})}
                  className="w-full pl-9 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                />
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Number of Properties
              </label>
              <input
                type="number"
                min="1"
                value={inputs.num_properties}
                onChange={(e) => setInputs({...inputs, num_properties: parseInt(e.target.value) || 1})}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Current Agent Fee (%)
              </label>
              <input
                type="number"
                min="0"
                max="20"
                step="0.5"
                value={inputs.current_management_fee_percent}
                onChange={(e) => setInputs({...inputs, current_management_fee_percent: parseFloat(e.target.value) || 0})}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              />
            </div>
            
            <button
              onClick={() => setShowAdvanced(!showAdvanced)}
              className="text-sm text-emerald-600 hover:text-emerald-700 font-medium"
            >
              {showAdvanced ? '− Hide advanced options' : '+ Show advanced options'}
            </button>
            
            {showAdvanced && (
              <div className="space-y-3 pt-2 border-t">
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="compliance"
                    checked={inputs.has_compliance_issues}
                    onChange={(e) => setInputs({...inputs, has_compliance_issues: e.target.checked})}
                    className="w-4 h-4 text-emerald-600 rounded"
                  />
                  <label htmlFor="compliance" className="text-sm text-gray-700">
                    Struggling with compliance
                  </label>
                </div>
                
                <div>
                  <label className="block text-sm text-gray-600 mb-1">Repairs per year</label>
                  <input
                    type="number"
                    value={inputs.num_repairs_per_year}
                    onChange={(e) => setInputs({...inputs, num_repairs_per_year: parseInt(e.target.value) || 0})}
                    className="w-full px-3 py-1.5 text-sm border rounded"
                  />
                </div>
                
                <div>
                  <label className="block text-sm text-gray-600 mb-1">Void days per year</label>
                  <input
                    type="number"
                    value={inputs.current_void_days_per_year}
                    onChange={(e) => setInputs({...inputs, current_void_days_per_year: parseInt(e.target.value) || 0})}
                    className="w-full px-3 py-1.5 text-sm border rounded"
                  />
                </div>
              </div>
            )}
            
            <Button 
              onClick={calculate} 
              disabled={loading}
              className="w-full bg-emerald-600 hover:bg-emerald-700"
            >
              {loading ? 'Calculating...' : 'Calculate Savings'}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </Card>
        
        {/* Results Section */}
        <div className="space-y-4">
          {!results ? (
            <Card className="p-6 bg-white/50 flex flex-col items-center justify-center min-h-[300px]">
              <TrendingDown className="w-12 h-12 text-emerald-300 mb-3" />
              <p className="text-gray-500 text-center">
                Enter your details and click calculate<br />to see your potential savings
              </p>
            </Card>
          ) : (
            <>
              {/* Current Costs */}
              <Card className="p-4 bg-red-50 border-red-100">
                <h4 className="text-sm font-medium text-red-800 mb-2">Your Current Annual Costs</h4>
                <div className="text-2xl font-bold text-red-600">
                  {formatCurrency(results.current_costs.total_annual)}
                </div>
                <div className="text-xs text-red-600 mt-1">
                  ({formatCurrency(results.current_costs.total_monthly)}/month)
                </div>
              </Card>
              
              {/* Tier Comparisons */}
              {results.tier_comparison.map((tier, idx) => (
                <Card 
                  key={tier.tier_name}
                  className={`p-4 border-2 ${
                    results.recommendation.tier === tier.tier_name.toLowerCase().replace('-', '_')
                      ? 'border-emerald-500 bg-emerald-50'
                      : 'border-transparent bg-white'
                  }`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <h4 className="font-bold">{tier.tier_name}</h4>
                      <span className="text-xs bg-gray-100 px-2 py-0.5 rounded">{tier.fee_percent}%</span>
                    </div>
                    {results.recommendation.tier === tier.tier_name.toLowerCase().replace('-', '_') && (
                      <span className="text-xs bg-emerald-600 text-white px-2 py-1 rounded-full flex items-center gap-1">
                        <CheckCircle className="w-3 h-3" />
                        Recommended
                      </span>
                    )}
                  </div>
                  
                  <div className="flex items-end gap-3">
                    <div>
                      <div className="text-xl font-bold text-gray-900">
                        {formatCurrency(tier.annual_fee)}
                        <span className="text-sm font-normal text-gray-500">/year</span>
                      </div>
                    </div>
                    <div className="flex-1" />
                    <div className="text-right">
                      <div className="text-lg font-bold text-emerald-600">
                        Save {formatCurrency(tier.annual_savings)}
                      </div>
                      <div className="text-xs text-emerald-700">
                        {tier.savings_percent.toFixed(1)}% less than current
                      </div>
                    </div>
                  </div>
                  
                  <div className="mt-3 pt-3 border-t">
                    <div className="flex flex-wrap gap-1">
                      {tier.features.slice(0, 4).map((feature, i) => (
                        <span key={i} className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded">
                          {feature}
                        </span>
                      ))}
                      {tier.features.length > 4 && (
                        <span className="text-xs text-gray-500">
                          +{tier.features.length - 4} more
                        </span>
                      )}
                    </div>
                  </div>
                </Card>
              ))}
              
              {/* Recommendation */}
              <Card className="p-4 bg-emerald-600 text-white">
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 mt-0.5 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold mb-1">Our Recommendation</h4>
                    <p className="text-sm text-emerald-100">
                      {results.recommendation.reason}
                    </p>
                  </div>
                </div>
              </Card>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
